import * as z from 'zod';

import { zodDateOrString } from '../../utils';

/**
 * #### Freddie Mac Electronic Default Reporting (EDR)
 * The servicer must report default information to Freddie Mac through Electronic Default Reporting
 * for any mortgage loan that was either:
 * - 30 or more days delinquent as of the last day of the preceding month; or
 * - for which an action was taken to manage the default during the preceding month.
 */

/**
 * ##### Action codes
 * Status codes: Reported every month until no longer applies
 * Event codes: Report once in the month after the event
 */
export const FreddieMacEDRActionCodes = z.enum([
  // Status codes
  '09', // Forbearance
  '12', // Repayment Plan
  '24', // Government Seizure
  '31', // Probate
  '32', // Military Indulgence
  '33', // Contested Foreclosure & Litigation
  '43', // Referred to Foreclosure
  '49', // HUD Assignment
  '59', // Chapter 12 Bankruptcy Petition Filed
  '61', // Second Lien Considerations
  '62', // VA—No Bid
  '63', // VA—Refund
  '64', // VA—Buydown
  '65', // Chapter 7 Bankruptcy Petition Filed
  '66', // Chapter 11 Bankruptcy Petition Filed
  '67', // Chapter 13 Bankruptcy Petition Filed
  'BF', // Freddie Mac Standard/Flex Trial Period
  'H6', // Payment Deferral Offer
  // Event codes
  '11', // Repayment Plan in Review
  '20', // Reinstatement (Partial)
  '35', // Bankruptcy Cramdown Scheduled
  '38', // Appeal of Loan Modification
  '68', // Date of First Legal Action
  '69', // Bankruptcy Plan Confirmed
  '71', // Foreclosure Sale Scheduled
  '74', // BPO/Appraisal Obtained
  '76', // Bankruptcy Court Clearance Obtained/Stay Lifted
  '80', // Date Breach Letter Sent
  'AW', // Date of First QRPC
  'AX', // Date of Last QRPC
  'AY', // Date Advised Property Listed for Sale
  'H3', // BPO Ordered
  'H4', // Solicitation Letter Sent
  'H5', // Complete BRP Received
  'HA', // Workout in Review
  'HB', // Short Sale in Review
  'HC', // Ineligible for Short Sale
  'HD', // Modification in Review
  'HE', // Ineligible/Cancel Modification
]);
export type FreddieMacEDRActionCode = z.infer<typeof FreddieMacEDRActionCodes>;

const FreddieMacEDRV1 = z.object({
  version: z.literal('1').describe('version'),

  cycleDate: zodDateOrString.describe('date'),

  /**
   * ##### Seller/Servicer Number (S/S-NO)
   * Your Freddie Mac Seller/Servicer number. 6 digits.
   */
  sellerServicerNumber: z.string().describe('string'),

  /**
   * ##### Freddie Mac Loan Number (FHLMC-LOAN-NO)
   * Freddie Mac–assigned loan number. 9 digits.
   */
  freddieMacLoanNumber: z.string().describe('string'),

  /**
   * ##### 1st Default Action Code (1ST-DEF-ACT-CODE)
   * Status/event for the loan this cycle.
   */
  firstDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### Default Reason Code (DEFAULT-REASON)
   * Reason for default (required once mortgage reaches 90+ DPD; earlier if in eligible disaster area or on active forbearance).
   * 3 characters.
   */
  defaultReasonCode: z
    .enum([
      '001', // Death of Principal Mortgagor
      '002', // Illness of Principal Mortgagor
      '003', // Illness of Mortgagor's Family Member
      '004', // Death of Mortgagor's Family Member
      '005', // Marital Difficulties
      '006', // Curtailment of Income
      '007', // Excessive Obligations
      '008', // Abandonment of Property
      '009', // Distant Employment Transfer
      '011', // Property Problem
      '012', // Inability to Sell Property
      '013', // Inability to Rent Property
      '014', // Military Service
      '015', // Other
      '016', // Unemployment
      '017', // Business Failure
      '019', // Casualty Loss
      '022', // Energy‑Environment Cost
      '023', // Servicing Problems
      '026', // Payment Adjustment
      '027', // Payment Dispute
      '029', // Transfer of Ownership Pending
      '030', // Fraud
      '031', // Unable to Contact Borrower
      '032', // National Emergency Declaration
      '034', // Eligible Disaster Area
      'INC', // Incarceration
    ])
    .optional()
    .describe('enum'),

  /**
   * ##### 1st Default Action Code Date (1ST-DEF-ACT-CODE-DT)
   * Action date for the 1st Default Action Code. Format: CCYYMMDD.
   */
  firstDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### Servicer Loan Number (S/S-LOAN-NO)
   * Your internal loan identifier. 13 characters.
   */
  LOSId: z.string().describe('string'),

  /**
   * ##### DDLPI (Due Date of Last Paid Installment)
   * The DDLPI for the loan. Format: YYMMDD.
   */
  LPIDate: zodDateOrString.describe('date'),

  /**
   * ##### Property Condition (CUR-PROP-COND)
   * Property condition code (required by 90th day of delinquency). 2 characters.
   */
  propertyCondition: z
    .enum([
      '03', // Excellent
      '04', // Good
      '05', // Fair
      '06', // Poor
      '10', // Unknown or No Property Inspection
      '18', // Property Inaccessible
      '20', // Condemned
    ])
    .optional()
    .describe('enum'),

  /**
   * ##### Occupancy Status (OCCUPY-STAT)
   * Occupancy status code (required by 90th day of delinquency). 2 characters.
   */
  occupancyStatus: z
    .enum([
      '01', // Vacant
      '03', // Borrower Occupied
      '04', // Tenant Occupied
      '06', // Unknown
      '07', // Abandoned
      '15', // Occupied by Unknown
    ])
    .optional()
    .describe('enum'),

  /**
   * ##### BPO or Appraised Value (BPO-APPR-VALUE)
   * If applicable, the BPO or appraisal value (numeric). 9 digits.
   */
  BPOOrAppraisedValue: z.number().optional().describe('currency'),

  /**
   * ##### 2nd Default Action Code (2ND-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  secondDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 2nd Default Action Code Date (2ND-DEF-ACT-CODE-DT)
   * Action date for the 2nd Default Action Code. Format: CCYYMMDD.
   */
  secondDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 3rd Default Action Code (3RD-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  thirdDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 3rd Default Action Code Date (3RD-DEF-ACT-CODE-DT)
   * Action date for the 3rd Default Action Code. Format: CCYYMMDD.
   */
  thirdDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 4th Default Action Code (4TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  fourthDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 4th Default Action Code Date (4TH-DEF-ACT-CODE-DT)
   * Action date for the 4th Default Action Code. Format: CCYYMMDD.
   */
  fourthDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 5th Default Action Code (5TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  fifthDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 5th Default Action Code Date (5TH-DEF-ACT-CODE-DT)
   * Action date for the 5th Default Action Code. Format: CCYYMMDD.
   */
  fifthDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### Filler
   * Single-space filler between the seller/servicer number and the 6th action code, per Freddie's spec.
   */
  filler: z.literal(' ').describe('filler'),

  /**
   * ##### 6th Default Action Code (6TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  sixthDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 6th Default Action Code Date (6TH-DEF-ACT-CODE-DT)
   * Action date for the 6th Default Action Code. Format: CCYYMMDD.
   */
  sixthDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 7th Default Action Code (7TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  seventhDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 7th Default Action Code Date (7TH-DEF-ACT-CODE-DT)
   * Action date for the 7th Default Action Code. Format: CCYYMMDD.
   */
  seventhDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 8th Default Action Code (8TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  eighthDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 8th Default Action Code Date (8TH-DEF-ACT-CODE-DT)
   * Action date for the 8th Default Action Code. Format: CCYYMMDD.
   */
  eighthDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 9th Default Action Code (9TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  ninthDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 9th Default Action Code Date (9TH-DEF-ACT-CODE-DT)
   * Action date for the 9th Default Action Code. Format: CCYYMMDD.
   */
  ninthDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 10th Default Action Code (10TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  tenthDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 10th Default Action Code Date (10TH-DEF-ACT-CODE-DT)
   * Action date for the 10th Default Action Code. Format: CCYYMMDD.
   */
  tenthDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 11th Default Action Code (11TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  eleventhDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 11th Default Action Code Date (11TH-DEF-ACT-CODE-DT)
   * Action date for the 11th Default Action Code. Format: CCYYMMDD.
   */
  eleventhDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 12th Default Action Code (12TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  twelfthDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 12th Default Action Code Date (12TH-DEF-ACT-CODE-DT)
   * Action date for the 12th Default Action Code. Format: CCYYMMDD.
   */
  twelfthDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 13th Default Action Code (13TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  thirteenthDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 13th Default Action Code Date (13TH-DEF-ACT-CODE-DT)
   * Action date for the 13th Default Action Code. Format: CCYYMMDD.
   */
  thirteenthDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 14th Default Action Code (14TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  fourteenthDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 14th Default Action Code Date (14TH-DEF-ACT-CODE-DT)
   * Action date for the 14th Default Action Code. Format: CCYYMMDD.
   */
  fourteenthDefaultActionCodeDate: zodDateOrString.optional().describe('date'),

  /**
   * ##### 15th Default Action Code (15TH-DEF-ACT-CODE)
   * Additional action this cycle, if applicable.
   */
  fifteenthDefaultActionCode: FreddieMacEDRActionCodes.optional().describe('enum'),

  /**
   * ##### 15th Default Action Code Date (15TH-DEF-ACT-CODE-DT)
   * Action date for the 15th Default Action Code. Format: CCYYMMDD.
   */
  fifteenthDefaultActionCodeDate: zodDateOrString.optional().describe('date'),
});
export type FreddieMacEDRV1 = z.infer<typeof FreddieMacEDRV1>;

// When we introduce a new version, make this a discriminated union
export const FreddieMacEDR = FreddieMacEDRV1;
export type FreddieMacEDR = z.infer<typeof FreddieMacEDR>;
